import * as pinia from 'pinia';
import { useLocalStorage } from '@vueuse/core';
import {
  CAMERA_TYPES,
  DEVICE_NAME,
  MEDIA_TYPE,
  DEVICE_TYPES,
  MEDIA_DEVICE_KIND,
  DISPLAY_NAME,
  VICE_SESSION_PHASES,
  DERMATOSCOPE_LABELS,
  OTOSCOPE_LABELS,
  MONITOR_LABELS,
  AUDIO_MEDICAL_DEVICES,
  VIDEO_MEDICAL_DEVICES,
  EXTERNAL_CONNECTED_AUDIO_DEVICES,
} from '@/Utils/vice-constants';
import JitsiService from '@/Services/JitsiService';
import useSessionStore from '@/Stores/useSessionStore';
import LogService from '@/Services/LogService';

const Jitsi = JitsiService();
const sessionStore = useSessionStore();
export const getCameraUniqId = (camName) => camName.toLowerCase().replace(/ /g, '_');

const useLocalStreamingDevicesStore = pinia.defineStore('LocalStreamingDevicesStore', {
  state: () => ({
    isRefreshed: true,
    shouldAddCamera: false,
    shouldMuteCamera: false,
    mediaTracksReady: false,
    externalAudioIO: false,
    devices: [],
    devicesWithDefault: [],
    isOpenAVSettingsModal: false,
    activeCameraType: CAMERA_TYPES.INTERNAL_WEBCAM,
    isPredefinedMainCameraDisconnected: false,
    isPredefinedMicDisconnected: false,
    cameraTypes: {
      [CAMERA_TYPES.INTERNAL_WEBCAM]: {
        uniqueId: getCameraUniqId(CAMERA_TYPES.INTERNAL_WEBCAM),
        cameraDeviceId: useLocalStorage(
          `_vice:${getCameraUniqId(CAMERA_TYPES.INTERNAL_WEBCAM)}:cameraDeviceId`,
          'default',
        ),
        micDeviceId: useLocalStorage(
          `_vice:${getCameraUniqId(CAMERA_TYPES.INTERNAL_WEBCAM)}:micDeviceId`,
          'default',
        ),
        speakerDeviceId: useLocalStorage(
          `_vice:${getCameraUniqId(CAMERA_TYPES.INTERNAL_WEBCAM)}:speakerDeviceId`,
          'default',
        ),
        localTracks: [],
        deviceSelection: {
          [DEVICE_NAME.MICROPHONE]: { active: false, choice: null },
          [DEVICE_NAME.SPEAKERS]: { active: false, choice: null },
          [DEVICE_NAME.CAMERA]: { active: false, choice: null },
        },
        jitsiRoom: null,
        jitsiInstance: null,
        videoRef: null,
        audioRef: null,
        videoAudioRefs: [],
        name: CAMERA_TYPES.INTERNAL_WEBCAM,
        image: `https://ui-avatars.com/api/?name=${CAMERA_TYPES.INTERNAL_WEBCAM}&bold=true&background=FFFFFF&color=24b8a6&format=PNG`,
        isCameraOn: useLocalStorage(
          `_vice:${getCameraUniqId(CAMERA_TYPES.INTERNAL_WEBCAM)}:isMyCameraOn`,
          false,
        ),
        isMicrophoneOn: useLocalStorage(
          `_vice:${getCameraUniqId(CAMERA_TYPES.INTERNAL_WEBCAM)}:isMyMicrophoneOn`,
          false,
        ),
        isActive: true,
      },
    },
    // TODO: rename and only add extra cameras
    connectedCameras: useLocalStorage(`_vice:cameraIds`, [
      {
        name: CAMERA_TYPES.INTERNAL_WEBCAM,
        cameraDeviceId: 'default',
      },
    ]),
    connectedExternalAudioDevices: {},
    connectedExternalVideoDevices: {},
    cachedExternalAudioDevices: useLocalStorage(`_vice:connectedExternalAudioDevices`, []),
    cachedExternalVideoDevices: useLocalStorage(`_vice:connectedExternalVideoDevices`, []),
    // On the multi preview grid we set the device being added.
    multiDeviceToAdd: '',
    externalDevicePreview: {
      [DISPLAY_NAME.VUZIX]: {
        name: DISPLAY_NAME.VUZIX,
        jitsiId: '',
        tracks: [],
        isCameraOn: true,
        isMicrophoneOn: true,
        isActive: false,
        videoRef: null,
        audioRef: null,
      },
      [DISPLAY_NAME.SCREENSHARE_USER]: {
        name: DISPLAY_NAME.SCREENSHARE_USER,
        jitsiId: '',
        tracks: [],
        isCameraOn: true,
        isMicrophoneOn: false,
        isActive: false,
        videoRef: null,
        audioRef: null,
      },
      [DISPLAY_NAME.STETHOSCOPE]: {
        name: DISPLAY_NAME.STETHOSCOPE,
        jitsiId: '',
        tracks: [],
        isCameraOn: true,
        isMicrophoneOn: false,
        isActive: false,
        videoRef: null,
        audioRef: null,
      },
    },
    showPreviewModal: false,
    mediaActivated: false,
    clinicianDevicesForSession: [],
    sessionSetupChanged: useLocalStorage(`_vice:sessionSetupChanged`, '0'),
    lastMicLabel: useLocalStorage(`_vice:lastMicLabel`, ''),
    lastSpkLabel: useLocalStorage(`_vice:lastSpkLabel`, ''),
    showMicSwitchedAlert: false,
    showSpkSwitchedAlert: false,
  }),
  getters: {
    devicesInitialized: (state) => state.devices.length,
    selectedCameraDeviceId: (state) => state.cameraTypes[state.activeCameraType]?.cameraDeviceId,
    selectedMicDeviceId: (state) =>
      state.getSelectedActualMicrophoneId() || state.getActualDefaultMicrophoneId(),
    selectedSpeakerDeviceId: (state) =>
      state.getSelectedActualSpeakersId() || state.getActualDefaultSpeakersId(),
    activeJitsiRoom: (state) => state.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].jitsiRoom,
    mediaDevices: (state) => {
      const groupedDevices = {
        [DEVICE_NAME.MICROPHONE]: [],
        [DEVICE_NAME.SPEAKERS]: [],
        [DEVICE_NAME.CAMERA]: [],
      };
      if (state.devices.length) {
        state.devices.forEach((device) => {
          if (device.kind === MEDIA_DEVICE_KIND.AUDIO_INPUT) {
            groupedDevices[DEVICE_NAME.MICROPHONE].push(device);
          } else if (device.kind === MEDIA_DEVICE_KIND.AUDIO_OUTPUT) {
            groupedDevices[DEVICE_NAME.SPEAKERS].push(device);
          } else if (device.kind === MEDIA_DEVICE_KIND.VIDEO_INPUT) {
            groupedDevices[DEVICE_NAME.CAMERA].push(device);
          }
        });
      }
      return groupedDevices;
    },
    groupedDeviceAsAudioAndVideo: (state) => ({
      AUDIO: {
        [DEVICE_NAME.MICROPHONE]: state.mediaDevices?.[DEVICE_NAME.MICROPHONE],
        [DEVICE_NAME.SPEAKERS]: state.mediaDevices?.[DEVICE_NAME.SPEAKERS],
      },
      VIDEO: { [DEVICE_NAME.CAMERA]: state.mediaDevices?.[DEVICE_NAME.CAMERA] },
    }),
    mediaSource: (state) => ({
      [DEVICE_NAME.MICROPHONE]: {
        id: state.mediaDevices[MEDIA_DEVICE_KIND.AUDIO_INPUT]?.[0].deviceId,
        label: state.mediaDevices[MEDIA_DEVICE_KIND.AUDIO_INPUT]?.[0].label,
      },
      [DEVICE_NAME.CAMERA]: {
        id: state.mediaDevices[MEDIA_DEVICE_KIND.VIDEO_INPUT]?.[0].deviceId,
        label: state.mediaDevices[MEDIA_DEVICE_KIND.VIDEO_INPUT]?.[0].label,
      },
      [DEVICE_NAME.SPEAKERS]: {
        id: state.mediaDevices[MEDIA_DEVICE_KIND.AUDIO_OUTPUT]?.[0].deviceId,
        label: state.mediaDevices[MEDIA_DEVICE_KIND.AUDIO_OUTPUT]?.[0].label,
      },
    }),
    activeDeviceLabels: (state) => ({
      [DEVICE_NAME.MICROPHONE]:
        state.mediaDevices[DEVICE_NAME.MICROPHONE]?.find(
          (device) => device.deviceId == state.selectedMicDeviceId,
        )?.label || 'Select microphone',
      [DEVICE_NAME.CAMERA]:
        state.mediaDevices[DEVICE_NAME.CAMERA]?.find(
          (device) => device.deviceId == state.selectedCameraDeviceId,
        )?.label ||
        state.mediaDevices[DEVICE_NAME.CAMERA][0]?.label ||
        'Select camera',
      [DEVICE_NAME.SPEAKERS]:
        state.mediaDevices[DEVICE_NAME.SPEAKERS]?.find(
          (device) => device.deviceId == state.selectedSpeakerDeviceId,
        )?.label ||
        state.mediaDevices[DEVICE_NAME.SPEAKERS][0]?.label ||
        'Select speakers',
    }),
    isOpenModal: (state) => state.isOpenAVSettingsModal,
    currentActiveCameraType: (state) => state.activeCameraType,
    allCameraTypes: (state) => state.cameraTypes,
    allCameraTypeNames: (state) => Object.keys(state.cameraTypes),
    isMediaTrackReady: (state) => state.mediaTracksReady,
    allExternalDevicePreview: (state) => state.externalDevicePreview,
    updatedInternalCamState: (state) =>
      !sessionStore.hasLivePhase &&
      sessionStore.currentPhase === VICE_SESSION_PHASES.INTRODUCTION &&
      sessionStore.isClinician
        ? true
        : state.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].isMicrophoneOn,
    allExternalConnectedAudioDevices: (state) => Object.keys(state.connectedExternalAudioDevices),
    allExternalConnectedVideoDevices: (state) => Object.keys(state.connectedExternalVideoDevices),
    allAvailableExternalVideoDevices: (state) => {
      const allDeviceLabels = DERMATOSCOPE_LABELS.concat(OTOSCOPE_LABELS).concat(MONITOR_LABELS);
      const externalVideoDevices = state.groupedDeviceAsAudioAndVideo.VIDEO.Camera.filter(
        (device) => allDeviceLabels.some((singleLabel) => device.label.includes(singleLabel)),
      );

      return externalVideoDevices;
    },
    allAvailableExternalMonitorDevices: (state) => {
      const allDeviceLabels = MONITOR_LABELS;
      const externalVideoDevices = state.groupedDeviceAsAudioAndVideo.VIDEO.Camera.filter(
        (device) => allDeviceLabels.some((singleLabel) => device.label.includes(singleLabel)),
      );

      return externalVideoDevices;
    },

    // Get list of labels of cameras thats connected to the session now
    getConnectedCamerasLabels: (state) => {
      const connectedCamerasIds = [];

      for (const [key, cameraType] of Object.entries(state.cameraTypes)) {
        cameraType.localTracks.forEach((localTrack) => {
          if (localTrack.type === MEDIA_TYPE.VIDEO) {
            const device = state.devices.find((obj) => obj.deviceId === localTrack.deviceId);
            if (device) {
              // here we need to check if external camera already connected to the session
              if (
                (key === CAMERA_TYPES.EXTERNAL_WEBCAM &&
                  state.connectedCameras.some((obj) => obj.cameraDeviceId === device.deviceId)) ||
                key === CAMERA_TYPES.INTERNAL_WEBCAM
              ) {
                connectedCamerasIds.push(device.label);
              }
            }
          }
        });
      }

      return connectedCamerasIds;
    },
    isSessionSetupWasChanged: (state) => state.sessionSetupChanged === sessionStore.sessionId,
  },

  actions: {
    // Cleanup cached devices from the previous session
    async actualiseCachedSessionDevicesSetup() {
      if (this.isSessionSetupWasChanged) return;

      await this.clearAllAdditionalDevicesFromCache();
    },

    async clearAllAdditionalDevicesFromCache() {
      this.sessionSetupChanged = '0';
      this.cachedExternalVideoDevices = [];
      this.cachedExternalAudioDevices = [];

      this.removeExternalCameraFromCache();
    },

    markSessionSetupAsChanged() {
      this.sessionSetupChanged = sessionStore.sessionId;
    },

    async checkPermissions() {
      const cameraPermission = await navigator.permissions.query({ name: 'camera' });
      const micPermission = await navigator.permissions.query({ name: 'microphone' });

      return {
        camera: cameraPermission.state === 'granted',
        microphone: micPermission.state === 'granted',
      };
    },

    async handleMediaDevicesChange() {
      await this.checkPermissions();
      await LogService.logDeviceListChanged(this.mediaDevices);

      await this.handleMicrophoneDisconnect();
      await this.handleSpeakersDisconnect();

      this.checkPredefinedMainCameraConnection();
      this.checkPredefinedMicroConnection();
    },

    checkPredefinedMainCameraConnection() {
      const predefinedCamLabel = this.clinicianDevicesForSession.clinician_devices?.find(
        (device) => device.device_type_label === DEVICE_TYPES.MAIN_CAMERA,
      )?.device_data?.label;

      if (!predefinedCamLabel) {
        this.isPredefinedMainCameraDisconnected = false;
        return;
      }

      const predefinedCamConnected = this.groupedDeviceAsAudioAndVideo.VIDEO.Camera?.some(
        (device) => device.label.includes(predefinedCamLabel),
      );

      this.isPredefinedMainCameraDisconnected = !predefinedCamConnected;
    },

    checkPredefinedMicroConnection() {
      const predefinedMicLabel = this.clinicianDevicesForSession.clinician_default_devices?.find(
        (device) => device.device_kind === MEDIA_DEVICE_KIND.AUDIO_INPUT,
      )?.device_data?.label;

      // If predefined mic not set, we don't consider it disconnected
      if (!predefinedMicLabel) {
        this.isPredefinedMicDisconnected = false;
        return;
      }

      const predefinedMicConnected = this.groupedDeviceAsAudioAndVideo.AUDIO.Microphone?.some(
        (device) => device.label.includes(predefinedMicLabel),
      );

      this.isPredefinedMicDisconnected = !predefinedMicConnected;
    },

    getActualPredefinedMic() {
      const predefinedMicLabel = this.clinicianDevicesForSession.clinician_default_devices?.find(
        (device) => device.device_kind === MEDIA_DEVICE_KIND.AUDIO_INPUT,
      )?.device_data?.label;

      return this.groupedDeviceAsAudioAndVideo.AUDIO.Microphone?.find((device) =>
        device.label.includes(predefinedMicLabel),
      );
    },

    getActualPredefinedSpeaker() {
      const predefinedSpeakerLabel =
        this.clinicianDevicesForSession.clinician_default_devices?.find(
          (device) => device.device_kind === MEDIA_DEVICE_KIND.AUDIO_OUTPUT,
        )?.device_data?.label;

      return this.groupedDeviceAsAudioAndVideo.AUDIO.Speakers?.find((device) =>
        device.label.includes(predefinedSpeakerLabel),
      );
    },

    async handleMicrophoneDisconnect() {
      const selectedDevice = await this.determineAppropriateDevice();
      if (!selectedDevice) {
        console.error('No connected microphone found');
        return;
      }

      this.updateMediaSource(selectedDevice);

      const shouldMuteMic = !sessionStore.isMyMicrophoneOn;
      await this.toggleMicrophone(CAMERA_TYPES.INTERNAL_WEBCAM, shouldMuteMic);
      await this.changeMicrophoneSource(this.activeCameraType);
    },

    async determineAppropriateDevice() {
      const actualMic = this.getActualDefaultMicrophone();

      if (['default', 'communications'].includes(this.selectedMicDeviceId)) return actualMic;

      const currentMic = this.mediaDevices?.Microphone?.find(
        (device) => device.deviceId === this.selectedMicDeviceId,
      );

      if (currentMic) return currentMic;

      return actualMic || this.mediaDevices?.Microphone?.[0];
    },

    updateMediaSource(device) {
      this.mediaSource.Microphone = {
        id: device.deviceId,
        label: device.label,
      };
    },

    async handleExternalDevicesDisconnect() {
      const audioRemovePromise = this.allExternalConnectedAudioDevices.map((externalAudioName) =>
        this.removeExternalAudioDevice(externalAudioName, false),
      );
      const videoRemovePromise = this.allExternalConnectedVideoDevices.map((externalVideoName) =>
        this.removeExternalVideoDevice(externalVideoName, false),
      );

      try {
        await Promise.all([...audioRemovePromise, ...videoRemovePromise]);
      } catch (error) {
        console.error({ error });
      }
    },

    // todo: use this instead of handleExternalDevicesDisconnect()?
    async handleExternalDevicesConnectivity() {
      const audioRemovePromise = this.allExternalConnectedAudioDevices.map((externalAudioName) => {
        const physicallyAvailable = !!this.groupedDeviceAsAudioAndVideo.AUDIO.Microphone.find(
          (device) =>
            AUDIO_MEDICAL_DEVICES.some((singleLabel) => device.label.includes(singleLabel)),
        );
        if (!physicallyAvailable) this.removeExternalAudioDevice(externalAudioName);
        return true;
      });

      const videoRemovePromise = this.allExternalConnectedVideoDevices.map((externalVideoName) => {
        const physicallyAvailable = !!this.groupedDeviceAsAudioAndVideo.VIDEO.Camera.find(
          (device) =>
            VIDEO_MEDICAL_DEVICES.some((singleLabel) => device.label.includes(singleLabel)),
        );
        if (!physicallyAvailable) this.removeExternalVideoDevice(externalVideoName);
        return true;
      });

      try {
        await Promise.all([...audioRemovePromise, ...videoRemovePromise]);
      } catch (error) {
        console.error({ error });
      }
    },

    async handleSpeakersDisconnect() {
      const selectedDevice = await this.determineAppropriateSpeaker();

      if (!selectedDevice) {
        console.error('No connected speakers');
        return;
      }

      this.updateSpeakerSource(selectedDevice);

      await this.setNewAudioOutput(
        this.activeCameraType,
        this.mediaSource.Speakers.id,
        this.mediaSource.Speakers.label,
      );
    },

    determineAppropriateSpeaker() {
      const { currentSpeaker, speakerGroupId } = this.findCurrentSpeaker();

      if (this.shouldUseAlternativeSpeaker(currentSpeaker)) {
        return this.findAlternativeSpeaker(speakerGroupId);
      }

      if (sessionStore.isClinician && currentSpeaker) return currentSpeaker;

      return this.getActualDefaultSpeakers();
    },

    findCurrentSpeaker() {
      let speakerGroupId = null;
      const currentSpeaker = this.mediaDevices?.Speakers?.find((device) => {
        if (device.deviceId === this.selectedSpeakerDeviceId) {
          speakerGroupId = device.groupId;
          return true;
        }
        return false;
      });

      return { currentSpeaker, speakerGroupId };
    },

    shouldUseAlternativeSpeaker(currentSpeaker) {
      return (
        !currentSpeaker ||
        this.selectedSpeakerDeviceId === 'default' ||
        this.selectedSpeakerDeviceId === 'communications'
      );
    },

    findAlternativeSpeaker(speakerGroupId) {
      if (!this.mediaDevices?.Speakers?.length) {
        return null;
      }

      if (speakerGroupId) {
        const sameGroupDevices = this.mediaDevices.Speakers.filter(
          (device) => device.groupId === speakerGroupId,
        );
        if (sameGroupDevices.length) {
          return sameGroupDevices[sameGroupDevices.length - 1];
        }
      }

      return this.mediaDevices.Speakers[0];
    },

    updateSpeakerSource(device) {
      this.mediaSource.Speakers = {
        id: device.deviceId,
        label: device.label,
      };
    },

    setVideoRef(el, cameraType) {
      if (el) this.cameraTypes[cameraType].videoRef = el;
    },
    setAudioRef(el, cameraType) {
      if (el) this.cameraTypes[cameraType].audioRef = el;
    },
    setExternalDeviceVideoRef(el, cameraType) {
      cameraType = 'Otoscope';
      console.log(el, cameraType);

      if (el) this.connectedExternalVideoDevices[cameraType].videoRef = el;
    },
    setExternalDeviceAudioRef(el, cameraType) {
      console.log(el, cameraType);
    },

    setPreviewVideoRef(el, displayName) {
      if (el) this.externalDevicePreview[displayName].videoRef = el;
    },
    setPreviewAudioRef(el, displayName) {
      if (el) this.externalDevicePreview[displayName].audioRef = el;
    },

    openPreviewModal(multiDeviceLabel) {
      this.setMultiDeviceToAdd(multiDeviceLabel);
      this.showPreviewModal = true;
    },

    setActiveJitsiRoom(cameraType, newJitsiRoom) {
      this.cameraTypes[cameraType].jitsiRoom = newJitsiRoom;
    },
    openAVSettingsModal() {
      this.isOpenAVSettingsModal = true;
    },
    closeAVSettingsModal() {
      this.isOpenAVSettingsModal = false;

      this.desactivateDeviceSelection();
      this.setCurrentActiveCameraType(CAMERA_TYPES.INTERNAL_WEBCAM);
    },

    setLocalMediaDevices(devicesData) {
      this.devicesWithDefault = devicesData;

      // get rid of the default and communications devices
      this.devices = devicesData.filter(
        (device) => device.deviceId !== 'default' && device.deviceId !== 'communications',
      );
    },

    setCurrentActiveCameraType(newCameraType) {
      this.activeCameraType = newCameraType;
    },

    toggleCameraTypeActiveState(cameraType, isActive) {
      this.cameraTypes[cameraType].isActive = isActive;
      if (isActive) {
        const deviceName =
          cameraType === CAMERA_TYPES.INTERNAL_WEBCAM ? DEVICE_TYPES.MAIN_CAMERA : cameraType;
        const videoTrack = this.cameraTypes[cameraType].localTracks.find(
          (track) => track.type === MEDIA_TYPE.VIDEO,
        );
        const audioTrack = this.cameraTypes[cameraType]?.localTracks?.find(
          (localTrack) => localTrack.getType() === MEDIA_TYPE.AUDIO,
        );

        // Create combined stream with both audio and video tracks
        const combinedStream = new MediaStream();
        if (videoTrack?.track) {
          combinedStream.addTrack(videoTrack.track);
        }
        if (audioTrack?.track) {
          combinedStream.addTrack(audioTrack.track);
        }

        // Add combined media stream to the store
        sessionStore.consultationStreams[deviceName.replace(/ /g, '_')] = combinedStream;
      }
    },

    async toggleCamera(incomingCameraType, shouldTurnCameraOn) {
      const cameraType = incomingCameraType || this.activeCameraType;
      const videoTrack = this.cameraTypes[cameraType]?.localTracks?.find(
        (localTrack) => localTrack.getType() === MEDIA_TYPE.VIDEO,
      );
      const audioTrack = this.cameraTypes[cameraType]?.localTracks?.find(
        (localTrack) => localTrack.getType() === MEDIA_TYPE.AUDIO,
      );

      const stillConnected = this.groupedDeviceAsAudioAndVideo.VIDEO.Camera.some(
        (device) => device.deviceId === this.cameraTypes[cameraType]?.cameraDeviceId,
      );

      if (!stillConnected) return;

      if (videoTrack) {
        const shouldMute =
          typeof shouldTurnCameraOn !== 'undefined' ? !shouldTurnCameraOn : !videoTrack.isMuted();

        if (shouldMute) {
          await videoTrack.mute();
        } else {
          await videoTrack.unmute();
          const deviceName =
            cameraType === CAMERA_TYPES.INTERNAL_WEBCAM ? DEVICE_TYPES.MAIN_CAMERA : cameraType;

          // Create combined stream with both audio and video tracks
          const combinedStream = new MediaStream();
          if (videoTrack?.track) {
            combinedStream.addTrack(videoTrack.track);
          }
          if (audioTrack?.track) {
            combinedStream.addTrack(audioTrack.track);
          }

          // Add combined media stream to the store
          sessionStore.consultationStreams[deviceName.replace(/ /g, '_')] = combinedStream;
        }
        // This syncs the visual toggles
        this.cameraTypes[cameraType].isCameraOn = !videoTrack.isMuted();

        if (cameraType === CAMERA_TYPES.INTERNAL_WEBCAM) {
          sessionStore.isMyCameraOn = !videoTrack.isMuted();
          this.updateActiveCallAVState(MEDIA_TYPE.VIDEO, !videoTrack.isMuted());
        }
      } else {
        this.activateMedia(cameraType);
      }
    },

    async toggleMicrophone(incomingCameraType, shouldMuteAudio) {
      if (!this.mediaActivated) return;
      const cameraType = incomingCameraType || this.activeCameraType;
      const audioTrack = this.cameraTypes[cameraType]?.localTracks?.find(
        (localTrack) => localTrack.getType() === MEDIA_TYPE.AUDIO,
      );

      if (audioTrack) {
        const shouldMute =
          typeof shouldMuteAudio !== 'undefined' ? shouldMuteAudio : !audioTrack.isMuted();

        if (shouldMute) {
          // eslint-disable-next-line no-await-in-loop
          await audioTrack.mute();
        } else {
          // eslint-disable-next-line no-await-in-loop
          await audioTrack.unmute();
        }
        // This syncs the visual toggles
        this.cameraTypes[cameraType].isMicrophoneOn = !audioTrack.isMuted();

        if (cameraType === CAMERA_TYPES.INTERNAL_WEBCAM) {
          sessionStore.isMyMicrophoneOn = !audioTrack.isMuted();
          this.updateActiveCallAVState(MEDIA_TYPE.AUDIO, !audioTrack.isMuted());
        }
      } else {
        await this.activateMedia(cameraType);
      }
    },

    async resetCameraState(shouldTurnCamOn) {
      Object.keys(this.cameraTypes).forEach(async (cameraType) => {
        const localVideoTrack = this.cameraTypes[cameraType]?.localTracks?.find(
          (localTrack) => localTrack.getType() === MEDIA_TYPE.VIDEO,
        );

        if (localVideoTrack) {
          if (shouldTurnCamOn) {
            // eslint-disable-next-line no-await-in-loop
            await localVideoTrack.unmute();
          } else {
            // eslint-disable-next-line no-await-in-loop
            await localVideoTrack.mute();
          }

          if (cameraType === CAMERA_TYPES.INTERNAL_WEBCAM) {
            sessionStore.isMyCameraOn = !localVideoTrack.isMuted();
            this.updateActiveCallAVState(MEDIA_TYPE.VIDEO, !localVideoTrack.isMuted());
          }
        } else {
          this.activateMedia(cameraType);
        }
      });
    },

    resetMicState(shouldTurnMicOn) {
      Object.keys(this.cameraTypes).forEach(async (cameraType) => {
        // since audio should come from only the internal camera, we reset audio state
        // for only the internal camera
        if (cameraType === CAMERA_TYPES.INTERNAL_WEBCAM) {
          const localAudioTrack = this.cameraTypes[cameraType]?.localTracks?.find(
            (localTrack) => localTrack.getType() === MEDIA_TYPE.AUDIO,
          );

          if (localAudioTrack) {
            if (shouldTurnMicOn) {
              // eslint-disable-next-line no-await-in-loop
              await localAudioTrack.unmute();
            } else {
              // eslint-disable-next-line no-await-in-loop
              await localAudioTrack.mute();
            }

            if (cameraType === CAMERA_TYPES.INTERNAL_WEBCAM) {
              sessionStore.isMyMicrophoneOn = !localAudioTrack.isMuted();
              this.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].isMicrophoneOn =
                !localAudioTrack.isMuted();
              this.updateActiveCallAVState(MEDIA_TYPE.AUDIO, !localAudioTrack.isMuted());
            }
          }
        } else {
          this.activateMedia(cameraType);
        }
      });
    },

    async updateMicrophoneState(cameraType, track) {
      // eslint-disable-next-line no-await-in-loop
      if (!this.updatedInternalCamState) await track.mute();

      // In the Live Phase, mute the attendee audio by default.
      // eslint-disable-next-line no-await-in-loop
      if (sessionStore.hasLivePhase && !sessionStore.isClinician) await track.mute();

      if (this.cameraTypes[cameraType].audioRef) {
        track.attach(this.cameraTypes[cameraType].audioRef);
      }

      this.cameraTypes[cameraType].isMicrophoneOn = !track.isMuted();
    },

    async attachMediaTracks(cameraType, tracks) {
      for (const track of tracks) {
        if (track.getType() === MEDIA_TYPE.VIDEO) {
          if (
            sessionStore.hasLivePhase &&
            sessionStore.currentPhase !== VICE_SESSION_PHASES.CONSULTATION
          ) {
            // eslint-disable-next-line no-await-in-loop
            if (!this.cameraTypes[cameraType].isCameraOn) await track.mute();
          }

          track.attach(this.cameraTypes[cameraType].videoRef);
          this.cameraTypes[cameraType].isCameraOn = !track.isMuted();
        }

        if (track.getType() === MEDIA_TYPE.AUDIO) {
          this.updateMicrophoneState(cameraType, track);
        }
        this.updateActiveCallAVState(track.getType(), !track.isMuted());
      }
    },

    setLocalTracks(cameraType, tracks) {
      if (
        this.cameraTypes[cameraType]?.localTracks.length === 2 &&
        cameraType === CAMERA_TYPES.INTERNAL_WEBCAM
      ) {
        const newVideo = tracks.find((el) => el.getType() === MEDIA_TYPE.VIDEO);
        this.cameraTypes[cameraType].localTracks.map((track) =>
          track.getType() === MEDIA_TYPE.VIDEO ? newVideo : track,
        );
      } else {
        this.cameraTypes[cameraType].localTracks = tracks;
      }

      // Attach the tracks to the preview display
      this.attachMediaTracks(cameraType, tracks);
    },

    setCameraDeviceId(cameraType, newCameraDeviceId) {
      if (!this.devicesInitialized) return;
      const deviceWithIdExists = !!this.mediaDevices[DEVICE_NAME.CAMERA]?.find(
        (device) => device.deviceId == newCameraDeviceId,
      );

      if (deviceWithIdExists) {
        this.cameraTypes[cameraType].cameraDeviceId = newCameraDeviceId;
      } else {
        this.cameraTypes[cameraType].cameraDeviceId =
          this.mediaDevices[DEVICE_NAME.CAMERA][0].deviceId;
      }
    },

    getSelectedActualMicrophoneId() {
      let actualMic = null;
      const predefinedMic = this.getActualPredefinedMic();

      if (this.isSessionSetupWasChanged) {
        // get cached mic
        actualMic = this.mediaDevices[DEVICE_NAME.MICROPHONE]?.find(
          (device) =>
            device.deviceId === this.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].micDeviceId,
        );
      } else {
        // get predefined mic
        actualMic = predefinedMic;
      }

      return actualMic?.deviceId || null;
    },

    getSelectedActualMicrophoneLabel() {
      const actualMic = this.mediaDevices[DEVICE_NAME.MICROPHONE]?.find(
        (device) => device.deviceId === this.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].micDeviceId,
      );

      return actualMic?.label || null;
    },

    getSelectedActualCameraLabel() {
      const actualCam = this.mediaDevices[DEVICE_NAME.CAMERA]?.find(
        (cam) => cam.deviceId === this.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].cameraDeviceId,
      );

      return actualCam?.label || null;
    },

    getActualDefaultMicrophone() {
      const defaultMic = this.devicesWithDefault.find(
        (device) => device.deviceId === 'default' && device.kind === 'audioinput',
      );

      const actualDefaultMic = this.mediaDevices[DEVICE_NAME.MICROPHONE]?.find(
        (device) => device?.groupId === defaultMic?.groupId,
      );

      return actualDefaultMic || defaultMic;
    },

    getActualDefaultMicrophoneId() {
      return this.getActualDefaultMicrophone()?.deviceId ?? 'default';
    },

    getSelectedActualSpeakersId() {
      let actualSpeakers = null;
      const predefinedSpeakers = this.getActualPredefinedSpeaker();

      if (this.isSessionSetupWasChanged) {
        // get cached speakers
        actualSpeakers = this.mediaDevices[DEVICE_NAME.SPEAKERS]?.find(
          (device) =>
            device.deviceId === this.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].speakerDeviceId,
        );
      } else {
        // get predefined speakers
        actualSpeakers = predefinedSpeakers;
      }

      return actualSpeakers?.deviceId || null;
    },

    getSelectedActualSpeakersLabel() {
      const actualSpeakers = this.mediaDevices[DEVICE_NAME.SPEAKERS]?.find(
        (device) =>
          device.deviceId === this.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].speakerDeviceId,
      );

      return actualSpeakers?.label || null;
    },

    getActualDefaultSpeakers() {
      const defaultSpeakers = this.devicesWithDefault.find(
        (device) => device.deviceId === 'default' && device.kind === 'audiooutput',
      );

      const actualDefaultSpeakers = this.mediaDevices[DEVICE_NAME.SPEAKERS]?.find(
        (device) => device?.groupId === defaultSpeakers?.groupId,
      );

      return actualDefaultSpeakers || defaultSpeakers;
    },

    getActualDefaultSpeakersId() {
      return this.getActualDefaultSpeakers()?.deviceId ?? 'default';
    },

    setMicDeviceId(cameraType, newMicDeviceId) {
      if (!this.devicesInitialized) return;

      if (['default', 'communications'].includes(newMicDeviceId)) {
        newMicDeviceId = this.getActualDefaultMicrophoneId();
      }

      const deviceWithIdExists = !!this.devicesWithDefault.find(
        (device) =>
          device.deviceId === newMicDeviceId && device.kind === MEDIA_DEVICE_KIND.AUDIO_INPUT,
      );

      if (deviceWithIdExists) {
        this.cameraTypes[cameraType].micDeviceId = newMicDeviceId;
      } else {
        this.cameraTypes[cameraType].micDeviceId =
          this.mediaDevices[DEVICE_NAME.MICROPHONE][0].deviceId;
      }
    },

    setSpeakerDeviceId(cameraType, newSpeakerDeviceId) {
      if (!this.devicesInitialized) return;

      if (['default', 'communications'].includes(newSpeakerDeviceId)) {
        newSpeakerDeviceId = this.getActualDefaultSpeakersId();
      }

      const deviceWithIdExists = !!this.mediaDevices[DEVICE_NAME.SPEAKERS]?.find(
        (device) => device.deviceId === newSpeakerDeviceId,
      );

      if (deviceWithIdExists) {
        this.cameraTypes[cameraType].speakerDeviceId = newSpeakerDeviceId;
      } else {
        this.cameraTypes[cameraType].speakerDeviceId =
          this.mediaDevices[DEVICE_NAME.SPEAKERS][0].deviceId;
      }
    },

    // Change Microphone source
    async changeMicrophoneSource(cameraType, byUser = false) {
      if (!cameraType) return;

      const cameraLocalTracks = this.cameraTypes[cameraType].localTracks;
      const audioTrack = cameraLocalTracks.find((track) => track.getType() === MEDIA_TYPE.AUDIO);
      const audioEl = this.cameraTypes[cameraType].audioRef;
      if (audioTrack) {
        await audioTrack.dispose();
        _.remove(cameraLocalTracks, (track) => track.getType() === MEDIA_TYPE.AUDIO);
        await audioTrack.detach(audioEl);
      }

      this.setMicDeviceId(cameraType, this.mediaSource[DEVICE_NAME.MICROPHONE].id);

      if (this.mediaSource[DEVICE_NAME.MICROPHONE].id === '') return;
      try {
        const newAudioTrack = await Jitsi.createAudioTrack(
          this.mediaSource[DEVICE_NAME.MICROPHONE].id,
        );
        cameraLocalTracks.push(newAudioTrack);

        if (
          !sessionStore.isMyMicrophoneOn ||
          !this.cameraTypes[cameraType].isMicrophoneOn ||
          cameraType === CAMERA_TYPES.EXTERNAL_WEBCAM
        ) {
          await newAudioTrack.mute();
        }
        if (audioEl) {
          newAudioTrack.attach(audioEl);
        }

        const conferenceAudioTrack = this.cameraTypes[cameraType]?.jitsiRoom?.getLocalAudioTrack();
        if (conferenceAudioTrack) {
          // eslint-disable-next-line no-await-in-loop
          await this.cameraTypes[cameraType].jitsiRoom?.replaceTrack(
            conferenceAudioTrack,
            newAudioTrack,
          );
        } else {
          // eslint-disable-next-line no-await-in-loop
          await this.cameraTypes[cameraType].jitsiRoom?.addTrack(newAudioTrack);
        }

        if (byUser) {
          this.showMicSwitchedAlert = false;
        }

        // Show warning mic has been switched
        if (
          !byUser &&
          cameraType === CAMERA_TYPES.INTERNAL_WEBCAM &&
          this.lastMicLabel !== '' &&
          this.lastMicLabel !== this.getSelectedActualMicrophoneLabel()
        ) {
          // Hide old alert if exists to reset labels
          this.showMicSwitchedAlert = false;
          setTimeout(() => {
            this.showMicSwitchedAlert = true;
          });
        }

        setTimeout(() => {
          this.lastMicLabel = this.getSelectedActualMicrophoneLabel();
        }, 1000);
      } catch (error) {
        console.error('Error getting tracks: ', error);
      }
    },

    // Change Camera source
    async setNewCameraSource(cameraType, cameraId = null) {
      const cameraLocalTracks = this.cameraTypes[cameraType].localTracks;
      const videoTrack = cameraLocalTracks.find((track) => track.getType() == MEDIA_TYPE.VIDEO);
      const videoEl = this.cameraTypes[cameraType].videoRef;

      if (videoTrack) {
        videoTrack.dispose();
        _.remove(cameraLocalTracks, (track) => track.getType() == MEDIA_TYPE.VIDEO);
        videoTrack.detach(videoEl);
      }

      const chosenCameraDeviceId = cameraId || this.mediaSource[DEVICE_NAME.CAMERA].id;
      this.setCameraDeviceId(cameraType, chosenCameraDeviceId);

      // Update selected cameraDeviceId
      const updatedCameraTypeIndex = this.connectedCameras.findIndex(
        (connectedCamera) => connectedCamera.name === cameraType,
      );

      if (updatedCameraTypeIndex !== -1) {
        this.connectedCameras[updatedCameraTypeIndex].cameraDeviceId = chosenCameraDeviceId;
      }

      try {
        const newVideoTrack = await Jitsi.createVideoTrack(
          this.cameraTypes[cameraType].cameraDeviceId,
        );
        cameraLocalTracks.push(newVideoTrack);

        // Attach new video track to the DOM
        newVideoTrack.attach(videoEl);
        if (!this.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].isCameraOn) {
          await newVideoTrack.mute();
        }
        // Add this new track to the active conference
        await this.cameraTypes[cameraType]?.jitsiRoom?.addTrack(newVideoTrack);
      } catch (error) {
        console.error('error getting tracks: ', error);
      }
    },

    closeDropdowns(cameraType) {
      let deviceSelection;
      if (!cameraType) {
        Object.values(CAMERA_TYPES).forEach((type) => {
          deviceSelection = Object.values(this.cameraTypes[type]?.deviceSelection || {});

          deviceSelection.forEach((device) => {
            device.active = false;
          });
        });
      } else {
        deviceSelection = this.cameraTypes[cameraType]?.deviceSelection;

        if (!deviceSelection) return;
        Object.keys(deviceSelection).forEach((device) => {
          deviceSelection[device].active = false;
        });
      }
    },

    changeAudioIOtoExternal(shouldChange = false) {
      /* We always mute the audio and mic on the vuzix
       * until a fix is made that allows us to toggle
       * the audio on the vuzix
       */
      this.externalAudioIO = shouldChange;
      sessionStore.deviceMessageSender.toggleSmartglassAudioIO(!shouldChange);
    },

    async setNewAudioOutput(cameraType, deviceId, label, byUser = false) {
      if (!cameraType) return;

      try {
        if (this.cameraTypes[cameraType].audioRef?.sinkId) {
          await this.cameraTypes[cameraType].audioRef.setSinkId(deviceId);
        }
        await Jitsi.changeAudioOutput(deviceId);
        this.mediaSource[DEVICE_NAME.SPEAKERS].id = deviceId;
        this.mediaSource[DEVICE_NAME.SPEAKERS].label = label;
        this.setSpeakerDeviceId(CAMERA_TYPES.INTERNAL_WEBCAM, deviceId);

        if (byUser) {
          this.showSpkSwitchedAlert = false;
        }

        // Show warning mic has been switched
        if (
          !byUser &&
          cameraType === CAMERA_TYPES.INTERNAL_WEBCAM &&
          this.lastSpkLabel !== '' &&
          this.lastSpkLabel !== this.getSelectedActualSpeakersLabel()
        ) {
          // Hide old alert if exists to reset labels
          this.showSpkSwitchedAlert = false;
          setTimeout(() => {
            this.showSpkSwitchedAlert = true;
          });
        }

        setTimeout(() => {
          this.lastSpkLabel = this.getSelectedActualSpeakersLabel();
        }, 1000);
      } catch (error) {
        console.error({ error });
      }
    },

    async chooseMediaSource(cameraType, deviceName, device = null) {
      this.handleDropdownsState(cameraType, deviceName);

      if (deviceName === DEVICE_NAME.SPEAKERS && device) {
        await this.setNewAudioOutput(cameraType, device.deviceId, device.label, true);
      }

      if (deviceName === DEVICE_NAME.MICROPHONE && device) {
        this.mediaSource[deviceName].id = ['default', 'communications'].includes(device.deviceId)
          ? this.getActualDefaultMicrophoneId()
          : device.deviceId;
        this.mediaSource[deviceName].label = device.label;
        await this.changeMicrophoneSource(cameraType, true);
      }

      if (deviceName === DEVICE_NAME.CAMERA && device) {
        this.mediaSource[deviceName].id = device.deviceId;
        this.mediaSource[deviceName].label = device.label;
        await this.setNewCameraSource(cameraType);
      }
    },

    handleDropdownsState(cameraType, deviceName) {
      if (this.cameraTypes[cameraType].deviceSelection[deviceName].active) {
        this.cameraTypes[cameraType].deviceSelection[deviceName].active = false;
      } else {
        this.cameraTypes[cameraType].deviceSelection[deviceName].active = true;
      }
    },

    async onLocalTracks(cameraType, tracks) {
      for (const track of tracks) {
        // eslint-disable-next-line no-await-in-loop
        await LogService.logLocalTrack(track, 'CREATED');

        track.track.addEventListener('ended', async () => {
          await LogService.logLocalTrack(track, 'ENDED');
        });
      }
      await JitsiMeetJS.mediaDevices.enumerateDevices(this.setLocalMediaDevices);
      sessionStore.hasPermissionError = false;
      // Set the initial media tracks gathered into all the camera types
      this.setLocalTracks(cameraType, tracks);
      this.mediaTracksReady = true;
    },

    // This may be moved into the JitsiService
    async activateMedia(cameraType) {
      this.mediaActivated = false;
      // Check if devices are available and could be accessed
      if (
        !(await JitsiMeetJS.mediaDevices.isDevicePermissionGranted())
        // || !JitsiMeetJS.mediaDevices.isDeviceListAvailable() ToDo: Check if still needed (deprecated at new Jitsi version)
      ) {
        sessionStore.hasPermissionError = true;
        console.error("We can't access your media");
      }
      const activeCameraType = cameraType || this.activeCameraType;

      // const activeMic = await JitsiMeetJS.getActiveAudioDevice();
      // if (this.selectedMicDeviceId === 'default') {
      //   this.setMicDeviceId(activeCameraType, activeMic.deviceId);
      // } else if (this.selectedMicDeviceId !== '') {
      //   this.setMicDeviceId(activeCameraType, this.selectedMicDeviceId);
      // }

      const cameraDeviceId =
        this.cameraTypes[activeCameraType].cameraDeviceId || this.selectedCameraDeviceId;

      const cameraStillConnected = this.groupedDeviceAsAudioAndVideo.VIDEO.Camera.some(
        (device) => device.deviceId === cameraDeviceId,
      );

      try {
        const mediaTracks = await JitsiMeetJS.createLocalTracks({
          devices: ['audio', 'video'],
          micDeviceId: this.selectedMicDeviceId,
          cameraDeviceId: cameraStillConnected ? cameraDeviceId : '',
          firePermissionPromptIsShownEvent: true,
        });

        const deviceName = DEVICE_TYPES.MAIN_CAMERA.replace(/ /g, '_');
        const videoTrack = mediaTracks.find((track) => track.type === MEDIA_TYPE.VIDEO);
        const audioTrack = mediaTracks.find((track) => track.type === MEDIA_TYPE.AUDIO);

        // Update selected devices id
        this.setMicDeviceId(activeCameraType, audioTrack.deviceId);
        this.setCameraDeviceId(activeCameraType, videoTrack.deviceId);

        // Create combined stream with both audio and video tracks
        const combinedStream = new MediaStream();
        if (videoTrack?.track) {
          combinedStream.addTrack(videoTrack.track);
        }
        if (audioTrack?.track) {
          combinedStream.addTrack(audioTrack.track);
        }

        // Add combined media stream to the store
        sessionStore.consultationStreams[deviceName.replace(/ /g, '_')] = combinedStream;

        this.onLocalTracks(activeCameraType, mediaTracks);
        this.mediaActivated = true;
      } catch (error) {
        throw new Error(error);
      }
    },

    async addCameraType(typeName, typeData) {
      this.cameraTypes = { ...this.cameraTypes, ...typeData };
      const cameraStillConnected = this.groupedDeviceAsAudioAndVideo.VIDEO.Camera.some(
        (device) => device.deviceId === typeData[typeName].cameraDeviceId,
      );
      // create new local tracks for this new camera.
      // this helps us manage them independently
      try {
        const mediaTracks = await JitsiMeetJS.createLocalTracks({
          devices: ['audio', 'video'],
          micDeviceId: typeData[typeName].micDeviceId,
          cameraDeviceId: cameraStillConnected ? typeData[typeName].cameraDeviceId : '',
          firePermissionPromptIsShownEvent: true,
        });
        // cache this one too so that you preserve the media devices
        JitsiMeetJS.mediaDevices.enumerateDevices((devices) => {
          this.setLocalMediaDevices(devices);
        });
        const deviceName =
          typeName === CAMERA_TYPES.INTERNAL_WEBCAM ? DEVICE_TYPES.MAIN_CAMERA : typeName;
        const videoTrack = mediaTracks.find((track) => track.type === MEDIA_TYPE.VIDEO);
        const audioTrack = mediaTracks.find((track) => track.type === MEDIA_TYPE.AUDIO);

        // Create combined stream with both audio and video tracks
        const combinedStream = new MediaStream();
        if (videoTrack?.track) {
          combinedStream.addTrack(videoTrack.track);
        }
        if (audioTrack?.track) {
          combinedStream.addTrack(audioTrack.track);
        }

        // Add combined media stream to the store
        sessionStore.consultationStreams[deviceName.replace(/ /g, '_')] = combinedStream;

        this.setLocalTracks(typeName, mediaTracks);
      } catch (error) {
        throw new Error(error);
      }
    },

    // Connect external audio devices to the session
    async addExternalAudioDevice(sessionId, externalAudioDeviceName, deviceLabels) {
      if (this.connectedExternalAudioDevices[externalAudioDeviceName]) return;
      try {
        // ToDo: fix adding device from cache
        const externalAudioDevice = this.groupedDeviceAsAudioAndVideo.AUDIO[
          DEVICE_NAME.MICROPHONE
        ].find((device) => deviceLabels.some((singleLabel) => device.label.includes(singleLabel)));

        // if device is not found
        if (!externalAudioDevice) return;

        /** Re-initialise to adjust constraints requirements */
        JitsiMeetJS.init({ disableAP: true });
        const audioTrack = await JitsiService().createAudioTrack(externalAudioDevice.deviceId);

        // Add stream to the store
        sessionStore.consultationStreams[externalAudioDeviceName.replace(/ /g, '_')] =
          audioTrack.stream;

        const externalAudioDeviceJitsiInstance = await JitsiService().makeConnection(sessionId);
        externalAudioDeviceJitsiInstance.room.setDisplayName(
          `Clinician-${externalAudioDeviceName}`,
        );
        externalAudioDeviceJitsiInstance.room.join();
        await externalAudioDeviceJitsiInstance.room.addTrack(audioTrack);
        this.connectedExternalAudioDevices = {
          ...this.connectedExternalAudioDevices,
          [externalAudioDeviceName]: {
            isActive: true,
            jitsiInstance: externalAudioDeviceJitsiInstance,
          },
        };

        this.addExternalAudioDeviceToCache(externalAudioDeviceName, deviceLabels);
      } catch (error) {
        throw new Error(error?.message ?? `Error adding ${externalAudioDeviceName}`);
      }
    },

    // Remove audio only devices connected to the session
    async removeExternalAudioDevice(externalAudioDeviceName, removeFromCache = true) {
      try {
        if (externalAudioDeviceName === EXTERNAL_CONNECTED_AUDIO_DEVICES.STETHOSCOPE) {
          /** Re-initialise to skip constraints requirements */
          JitsiMeetJS.init({ disableAP: false });
        }

        if (removeFromCache) {
          this.removeExternalAudioDeviceFromCache(externalAudioDeviceName);
        }

        // Remove stream from the store
        delete sessionStore.consultationStreams[externalAudioDeviceName.replace(/ /g, '_')];

        if (this.connectedExternalAudioDevices[externalAudioDeviceName]) {
          await this.connectedExternalAudioDevices[
            externalAudioDeviceName
          ].jitsiInstance.disconnect();
          await this.connectedExternalAudioDevices[
            externalAudioDeviceName
          ].jitsiInstance.closeConnection();
          delete this.connectedExternalAudioDevices[externalAudioDeviceName];
        }
      } catch (error) {
        throw new Error(error?.message ?? `Error removing ${externalAudioDeviceName}`);
      }
    },

    // Connect external video devices to the session
    async addExternalVideoDevice(sessionId, externalVideoDeviceName, deviceLabels) {
      try {
        if (this.connectedExternalVideoDevices[externalVideoDeviceName]) return;
        const externalVideoDevice = this.groupedDeviceAsAudioAndVideo.VIDEO.Camera.find((device) =>
          deviceLabels.some((singleLabel) => device.label.includes(singleLabel)),
        );

        // if device is not found
        if (!externalVideoDevice) return;

        const videoTrack = await JitsiService().createVideoTrack(externalVideoDevice.deviceId);

        // Add media stream to the store
        sessionStore.consultationStreams[externalVideoDeviceName.replace(/ /g, '_')] =
          videoTrack.stream;

        const externalVideoDeviceJitsiInstance = await JitsiService().makeConnection(sessionId);
        externalVideoDeviceJitsiInstance.room.setDisplayName(
          `Clinician-${externalVideoDeviceName}`,
        );
        externalVideoDeviceJitsiInstance.room.join();
        await externalVideoDeviceJitsiInstance.room.addTrack(videoTrack);
        this.connectedExternalVideoDevices = {
          ...this.connectedExternalVideoDevices,
          [externalVideoDeviceName]: {
            isActive: true,
            jitsiInstance: externalVideoDeviceJitsiInstance,
          },
        };
        this.addExternalVideoDeviceToCache(externalVideoDeviceName, deviceLabels);
      } catch (error) {
        throw new Error(error?.message ?? `Error adding ${externalVideoDeviceName}`);
      }
    },

    // Remove audio only devices connected to the session
    async removeExternalVideoDevice(externalVideoDeviceName, removeFromCache = true) {
      try {
        if (removeFromCache) {
          this.removeExternalVideoDeviceFromCache(externalVideoDeviceName);
        }

        // Remove media stream from the store
        delete sessionStore.consultationStreams[externalVideoDeviceName.replace(/ /g, '_')];

        if (this.connectedExternalVideoDevices[externalVideoDeviceName]) {
          await this.connectedExternalVideoDevices[
            externalVideoDeviceName
          ].jitsiInstance.disconnect();
          await this.connectedExternalVideoDevices[
            externalVideoDeviceName
          ].jitsiInstance.closeConnection();
          delete this.connectedExternalVideoDevices[externalVideoDeviceName];
        }
      } catch (error) {
        throw new Error(error?.message ?? `Error removing ${externalVideoDeviceName}`);
      }
    },

    // Add EC to cache
    addExternalCameraToCache(deviceId, label = null) {
      const newCam = {
        name: CAMERA_TYPES.EXTERNAL_WEBCAM,
        cameraDeviceId: deviceId,
        label,
      };
      // Remove the previous cached camera if exists
      this.removeExternalCameraFromCache();

      // Add a new one
      this.connectedCameras = [...this.connectedCameras, newCam];
    },

    // Remove EC from the cache
    removeExternalCameraFromCache() {
      this.connectedCameras = this.connectedCameras.filter(
        (camera) => camera.name !== CAMERA_TYPES.EXTERNAL_WEBCAM,
      );
    },

    // Add VD to cache
    addExternalVideoDeviceToCache(externalVideoDeviceName, deviceLabels) {
      const videoDeviceAlreadyCached = this.cachedExternalVideoDevices.some(
        (videoDevice) => videoDevice.name === externalVideoDeviceName,
      );

      if (!videoDeviceAlreadyCached) {
        this.cachedExternalVideoDevices = [
          ...this.cachedExternalVideoDevices,
          {
            name: externalVideoDeviceName,
            deviceLabels: JSON.stringify(deviceLabels),
          },
        ];
      }
    },

    // Remove VD form cache
    removeExternalVideoDeviceFromCache(externalVideoDeviceName) {
      this.cachedExternalVideoDevices = this.cachedExternalVideoDevices.filter(
        (vidDevice) => vidDevice.name !== externalVideoDeviceName,
      );
    },

    // Add AD to cache
    addExternalAudioDeviceToCache(externalAudioDeviceName, deviceLabels) {
      const audioDeviceAlreadyCached = this.cachedExternalAudioDevices.some(
        (audioDevice) => audioDevice.name === externalAudioDeviceName,
      );

      if (!audioDeviceAlreadyCached) {
        this.cachedExternalAudioDevices = [
          ...this.cachedExternalAudioDevices,
          {
            name: externalAudioDeviceName,
            deviceLabels: JSON.stringify(deviceLabels),
          },
        ];
      }
    },

    // Remove AD form cache
    removeExternalAudioDeviceFromCache(externalAudioDeviceName) {
      this.cachedExternalAudioDevices = this.cachedExternalAudioDevices.filter(
        (audDevice) => audDevice.name !== externalAudioDeviceName,
      );
    },

    /**
     * Initialize Jitsi for additionally attached cameras
     * @param {*} sessionId
     */
    initializeJitsi(sessionId) {
      Object.keys(this.cameraTypes).forEach(async (cameraType) => {
        if (cameraType !== CAMERA_TYPES.INTERNAL_WEBCAM && this.cameraTypes[cameraType].isActive) {
          // Create a very fresh instance using this closure.
          // Jitsi does not allow adding multiple video tracks
          // to the same instance. Room/conference Instances should be unique
          // eslint-disable-next-line no-await-in-loop
          const cameraJitsiInstance = await JitsiService().makeConnection(sessionId);
          this.cameraTypes[cameraType].jitsiRoom = cameraJitsiInstance.room;
          this.cameraTypes[cameraType].jitsiInstance = cameraJitsiInstance;

          // Set display name for this camera
          cameraJitsiInstance.room.setDisplayName(`Clinician-${cameraType}`);

          // Join the room
          cameraJitsiInstance.room.join();

          // add the tracks to the room.
          // Camera should always be unmuted but audio should be muted before adding to the room
          for (const track of this.cameraTypes[cameraType].localTracks) {
            if (track.getType() === MEDIA_TYPE.VIDEO) {
              // eslint-disable-next-line no-await-in-loop
              await track.unmute();
            }
            if (track.getType() === MEDIA_TYPE.AUDIO) {
              // eslint-disable-next-line no-await-in-loop
              await track.mute();
            }
            // eslint-disable-next-line no-await-in-loop
            await cameraJitsiInstance.room.addTrack(track);
          }
        }
      });
    },

    disableMultiCameraStream() {
      Object.keys(this.cameraTypes).forEach(async (cameraType) => {
        await this.cameraTypes[cameraType].jitsiInstance?.disconnect();
      });
    },

    updateActiveCallAVState(trackType, trackState) {
      sessionStore.addActiveCallParticipants({
        [sessionStore.authUser.id]: {
          [trackType]: trackState,
          isRemoteUser: false,
        },
      });
    },

    async endCall(cameraType) {
      const activeCameraType = cameraType || this.activeCameraType;
      if (this.cameraTypes?.[activeCameraType]?.localTracks?.length) {
        for (const track of this.cameraTypes[activeCameraType].localTracks) {
          track.dispose();
          if (track.getType() === MEDIA_TYPE.VIDEO) {
            const videoEl = this.cameraTypes[activeCameraType].videoRef;
            track.detach(videoEl);
          }
          if (track.getType() === MEDIA_TYPE.AUDIO) {
            const audioEl = this.cameraTypes[activeCameraType].audioRef;
            track.detach(audioEl);
          }
          // calling stop on the media track directly to turn off the camera
          track.track.stop();
        }

        // Empty the refs and localTracks array so that we always fetch new one
        this.cameraTypes[activeCameraType].localTracks = [];
        this.cameraTypes[activeCameraType].audioRef = null;
        this.cameraTypes[activeCameraType].videoRef = null;
      }
    },

    async removeCamera(cameraToBeRemoved, removeFromCache = true) {
      this.setCurrentActiveCameraType(CAMERA_TYPES.INTERNAL_WEBCAM);
      // If camera is connected to a jitsi room, this removes it altogether
      await this.cameraTypes[cameraToBeRemoved]?.jitsiInstance?.disconnect();
      await this.cameraTypes[cameraToBeRemoved]?.jitsiInstance?.closeConnection();
      delete this.cameraTypes[cameraToBeRemoved]; // ??
      const deviceName =
        cameraToBeRemoved === DEVICE_TYPES.MAIN_CAMERA
          ? DEVICE_TYPES.MAIN_CAMERA
          : cameraToBeRemoved;

      delete sessionStore.consultationStreams[deviceName.replace(/ /g, '_')];

      if (removeFromCache) {
        this.removeExternalCameraFromCache();
      }
    },

    setMultiDeviceToAdd(multiDeviceName) {
      this.multiDeviceToAdd = multiDeviceName;
    },

    updateExternalDevicePreview(displayName, prop, value) {
      this.externalDevicePreview[displayName][prop] = value;
    },
    removeExternalDevicePreview(displayName) {
      if (displayName == DISPLAY_NAME.VUZIX) {
        sessionStore.deviceMessageSender.turnOffSmartGlass();
      }
      this.externalDevicePreview[displayName] = {
        name: displayName,
        jitsiId: '',
        tracks: [],
        isCameraOn: true,
        isMicrophoneOn: false,
        isActive: false,
        videoRef: null,
        audioRef: null,
      };
    },

    async addExternalDevicesFromCache(sessionId) {
      const cachedAudioPromise = this.cachedExternalAudioDevices.map((audioDevice) =>
        this.addExternalAudioDevice(
          sessionId,
          audioDevice.name,
          JSON.parse(audioDevice.deviceLabels),
        ),
      );
      const cachedVideoPromise = this.cachedExternalVideoDevices.map((vidDevice) =>
        this.addExternalVideoDevice(sessionId, vidDevice.name, JSON.parse(vidDevice.deviceLabels)),
      );

      try {
        await Promise.all([...cachedAudioPromise, ...cachedVideoPromise]);
      } catch (error) {
        console.error('addExternalDevicesFromCache: error: ', error);
      }
    },

    // delete all external cameras
    clearExternalCamerasList() {
      Object.keys(this.cameraTypes).forEach(async (cameraTypeKey) => {
        if (cameraTypeKey.includes(CAMERA_TYPES.EXTERNAL_WEBCAM)) {
          delete this.cameraTypes[cameraTypeKey];
        }
      });
    },

    // related to VCE-1676

    // If you expand the list of cameras in the Audio and video settings modal window, the storage
    // value LocalStreamingDevicesStore.cameraTypes.Internal Webcam.deviceSelection.Camera.active will be set to true.
    // If you do not select a camera or close the list, but simply close the settings window, this value will remain true.
    // That is, the next time you open the window, the list will be expanded.

    // In order to prevent this, code is assigned to the event of closing the settings window,
    // which will runs all the elements in LocalStreamingDevicesStore.cameraTypes and force the active value to false.
    desactivateDeviceSelection() {
      for (const [cameraTypeKey, cameraType] of Object.entries(this.cameraTypes)) {
        for (const [devKey] of Object.entries(cameraType.deviceSelection)) {
          this.cameraTypes[cameraTypeKey].deviceSelection[devKey].active = false;
        }
      }
    },

    setMainCameraDeviceId(deviceId) {
      this.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].cameraDeviceId = deviceId;
    },

    setPredefinedMainCameraDisconnected(isDisconnected) {
      this.isPredefinedMainCameraDisconnected = isDisconnected;
    },
  },
});

export default useLocalStreamingDevicesStore;
